import React, { useRef, useState } from 'react';
import SITE from '../api';
import { ProductCard } from './Home';
import { usePortal, PORTAL } from '../portalAuth';
import { STORE_BASE } from '../routeBase';

// "Search by photo" — upload a picture of a worn/faded frame, or any similar
// eyewear, and find the closest-matching models in the catalogue by visual
// similarity (an on-server AI image match, not a text search).
export default function VisualSearch() {
  const { customer } = usePortal();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const runSearch = async (file) => {
    if (!file) return;
    setErr(''); setResults(null);
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      // Logged-in customers get their own priced results from the portal
      // API (same pattern as the main product listing) — guests get the
      // public, price-less one.
      const client = customer ? PORTAL : SITE;
      const r = await client.post('/visual-search', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResults(r.data.products || []);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not search — please try another photo.');
    }
    setBusy(false);
  };

  const onFile = e => runSearch(e.target.files?.[0]);
  const onDrop = e => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && /^image\//.test(file.type)) runSearch(file);
  };

  return (
    <div style={{ maxWidth: 1100, margin: '30px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 6 }}>Search by Photo</h1>
      <p style={{ color: '#777', fontSize: 14, marginBottom: 24 }}>
        Upload a photo of your worn or faded frame — or any eyewear you like the look of — and we'll find the closest matches in our catalogue.
      </p>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--sf-primary,#191919)' : '#ddd'}`, borderRadius: 14,
          padding: 32, textAlign: 'center', cursor: 'pointer', background: dragOver ? '#fafafa' : '#fff',
          transition: 'border-color .15s, background .15s',
        }}>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
        {preview ? (
          <img src={preview} alt="Your upload" style={{ maxWidth: 220, maxHeight: 220, borderRadius: 10, objectFit: 'contain' }} />
        ) : (
          <>
            <i className="ti ti-camera" style={{ fontSize: 40, color: '#bbb' }} />
            <div style={{ fontSize: 14.5, fontWeight: 600, marginTop: 10 }}>Drop a photo here, or click to choose one</div>
            <div style={{ fontSize: 12.5, color: '#999', marginTop: 4 }}>JPG or PNG, up to 8MB</div>
          </>
        )}
        {preview && <div style={{ fontSize: 12.5, color: 'var(--sf-accent)', marginTop: 10, fontWeight: 600 }}>Click to try a different photo</div>}
      </div>

      {busy && <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>Analysing your photo…</div>}
      {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '10px 14px', borderRadius: 8, marginTop: 16 }}>{err}</div>}

      {results && (
        <div style={{ marginTop: 30 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
            {results.length ? `${results.length} closest matches` : 'No close matches found'}
          </h3>
          {results.length === 0 && <div style={{ color: '#999', fontSize: 13.5 }}>Try a clearer, front-on photo, or browse the full <a href={`${STORE_BASE}/shop`} style={{ color: 'var(--sf-accent)' }}>Shop</a> instead.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16 }}>
            {results.map(p => (
              <div key={p.model_no} style={{ position: 'relative' }}>
                {/* left side — the right side is where ProductCard puts its
                    own wishlist/compare icons, and the two were overlapping */}
                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, background: 'rgba(25,25,25,0.85)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 12 }}>
                  {p.similarity}% match
                </div>
                <ProductCard p={p} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
